import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../../components/ui/dropdown-menu";
import { Truck, Calendar as CalendarIcon, Loader2, MoreVertical, CheckCircle, XCircle, User, Building2, Plus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { format, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminBookings() {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [vehiclesList, setVehiclesList] = useState([]);
  const [customers, setCustomers] = useState({});
  const [customersList, setCustomersList] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  // Create booking dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, statusFilter]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = statusFilter ? `?status=${statusFilter}` : "";
      
      const [bookingsRes, vehiclesRes, usersRes, companiesRes] = await Promise.all([
        axios.get(`${API}/bookings${params}`, { headers, withCredentials: true }),
        axios.get(`${API}/vehicles`, { headers, withCredentials: true }),
        axios.get(`${API}/users`, { headers, withCredentials: true }),
        axios.get(`${API}/companies`, { headers, withCredentials: true })
      ]);
      
      setBookings(bookingsRes.data);
      setVehiclesList(vehiclesRes.data);
      setCustomersList(usersRes.data.filter(u => u.role === "customer"));
      
      // Index by ID
      const vehicleMap = {};
      vehiclesRes.data.forEach(v => vehicleMap[v.vehicle_id] = v);
      setVehicles(vehicleMap);
      
      const userMap = {};
      usersRes.data.forEach(u => userMap[u.user_id] = u);
      setCustomers(userMap);
      
      const companyMap = {};
      companiesRes.data.forEach(c => companyMap[c.company_id] = c);
      setCompanies(companyMap);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/bookings/${bookingId}/status`, { status: newStatus }, { headers, withCredentials: true });
      toast.success(`Prenotazione ${getStatusLabel(newStatus).toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  const getSelectedVehicle = () => {
    return vehiclesList.find(v => v.vehicle_id === selectedVehicle);
  };

  const calculatePrice = () => {
    const vehicle = getSelectedVehicle();
    if (!startDate || !endDate || !vehicle) return 0;
    
    const days = differenceInDays(endDate, startDate);
    if (days < 1) return vehicle.daily_rate;
    
    if (days >= 7 && vehicle.weekly_rate) {
      const weeks = Math.floor(days / 7);
      const remainingDays = days % 7;
      return (weeks * vehicle.weekly_rate) + (remainingDays * vehicle.daily_rate);
    }
    
    return days * vehicle.daily_rate;
  };

  const handleCreateBooking = async () => {
    if (!selectedCustomer || !selectedVehicle || !startDate || !endDate) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // We need to create booking on behalf of customer
      // First, we'll use a special admin endpoint or simulate the request
      await axios.post(`${API}/bookings/admin`, {
        customer_id: selectedCustomer,
        vehicle_id: selectedVehicle,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        notes: notes || null
      }, { headers, withCredentials: true });

      toast.success("Prenotazione creata con successo!");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la creazione");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCustomer("");
    setSelectedVehicle("");
    setStartDate(null);
    setEndDate(null);
    setNotes("");
  };

  return (
    <DashboardLayout title="Tutte le Prenotazioni">
      <div className="space-y-6" data-testid="admin-bookings">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="confirmed">Confermate</SelectItem>
                <SelectItem value="completed">Completate</SelectItem>
                <SelectItem value="cancelled">Cancellate</SelectItem>
              </SelectContent>
            </Select>
            
            <p className="text-sm text-muted-foreground">
              {bookings.length} prenotazioni
            </p>
          </div>

          <Button onClick={() => setShowCreateDialog(true)} className="btn-primary py-2" data-testid="create-booking-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuova Prenotazione
          </Button>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : bookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nessuna prenotazione</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {statusFilter ? "Nessuna prenotazione con questo stato" : "Non ci sono prenotazioni nel sistema"}
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                Crea la prima prenotazione
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => {
              const vehicle = vehicles[booking.vehicle_id];
              const customer = customers[booking.customer_id];
              const company = companies[booking.company_id];
              
              return (
                <Card key={booking.booking_id} data-testid={`booking-${booking.booking_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="bg-muted w-24 h-16 flex items-center justify-center">
                          {vehicle?.images?.[0] ? (
                            <img
                              src={vehicle.images[0].startsWith('http') ? vehicle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Truck className="h-8 w-8 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusColor(booking.status)}>
                              {getStatusLabel(booking.status)}
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              #{booking.booking_id.slice(-8)}
                            </span>
                          </div>
                          <h3 className="font-bold">
                            {vehicle ? `${vehicle.brand} ${vehicle.model}` : "Veicolo"}
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <User className="h-4 w-4" />
                              <span>{customer?.name || "N/A"}</span>
                              <span className="text-xs">({customer?.email || "N/A"})</span>
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Building2 className="h-4 w-4" />
                              <span>{company?.name || "N/A"}</span>
                            </div>
                          </div>
                          
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <CalendarIcon className="h-4 w-4" />
                            {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                          </p>
                          
                          {booking.notes && (
                            <p className="text-sm text-muted-foreground mt-1">Note: {booking.notes}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground uppercase">Totale</p>
                          <p className="text-xl font-bold font-mono text-primary">
                            {formatCurrency(booking.total_price)}
                          </p>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`booking-actions-${booking.booking_id}`}>
                              <MoreVertical className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {booking.status === "pending" && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => updateStatus(booking.booking_id, "confirmed")}
                                  className="text-green-600"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Conferma
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateStatus(booking.booking_id, "cancelled")}
                                  className="text-red-600"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Cancella
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.status === "confirmed" && (
                              <DropdownMenuItem 
                                onClick={() => updateStatus(booking.booking_id, "completed")}
                                className="text-blue-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Segna come Completata
                              </DropdownMenuItem>
                            )}
                            {(booking.status === "completed" || booking.status === "cancelled") && (
                              <DropdownMenuItem disabled>
                                Nessuna azione disponibile
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Booking Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                Nuova Prenotazione
              </DialogTitle>
              <DialogDescription>
                Crea una prenotazione per conto di un cliente
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Customer Selection */}
              <div>
                <Label className="input-label">Cliente *</Label>
                <Select value={selectedCustomer || "select"} onValueChange={(v) => setSelectedCustomer(v === "select" ? "" : v)}>
                  <SelectTrigger data-testid="booking-customer">
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Seleziona cliente</SelectItem>
                    {customersList.map((c) => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {c.name} ({c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {customersList.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Nessun cliente registrato nel sistema
                  </p>
                )}
              </div>

              {/* Vehicle Selection */}
              <div>
                <Label className="input-label">Veicolo *</Label>
                <Select value={selectedVehicle || "select"} onValueChange={(v) => setSelectedVehicle(v === "select" ? "" : v)}>
                  <SelectTrigger data-testid="booking-vehicle">
                    <SelectValue placeholder="Seleziona veicolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Seleziona veicolo</SelectItem>
                    {vehiclesList.filter(v => v.is_available !== false).map((v) => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.brand} {v.model} - {formatCurrency(v.daily_rate)}/giorno
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vehiclesList.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Nessun veicolo disponibile nel sistema
                  </p>
                )}
              </div>

              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Data Inizio *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="booking-start-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "dd/MM/yyyy", { locale: it }) : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        disabled={(date) => date < new Date()}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="input-label">Data Fine *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="booking-end-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "dd/MM/yyyy", { locale: it }) : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => date < (startDate || new Date())}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="input-label">Note (opzionale)</Label>
                <Textarea
                  placeholder="Aggiungi eventuali note..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="booking-notes"
                />
              </div>

              {/* Price Summary */}
              {startDate && endDate && selectedVehicle && (
                <div className="bg-muted/50 p-4 border border-border">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Durata</span>
                    <span className="font-mono">{differenceInDays(endDate, startDate) || 1} giorni</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Totale Stimato</span>
                    <span className="text-primary font-mono">{formatCurrency(calculatePrice())}</span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Annulla
              </Button>
              <Button
                className="btn-primary py-2"
                onClick={handleCreateBooking}
                disabled={submitting || !selectedCustomer || !selectedVehicle || !startDate || !endDate}
                data-testid="confirm-create-booking"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  "Crea Prenotazione"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
